import { ScrollArea } from '~/primitives/scroll-area';
import { ListTabSwitch } from '~/partials/TabSwitch';
import ThemedImage from '~/partials/ThemedImage';
import { useTheme } from '~/components/Providers/ThemeContext';
import chroma from 'chroma-js';
import { cn } from '~/util';
import {
    ClanMember,
    ClanMemberWithContributedPoints,
} from '~/util/types/clans';
import {
    Crown,
    Pencil,
    Plus,
    Settings,
    SquareArrowRightIcon,
} from 'lucide-react';
import { Button } from '~/primitives/button';
import { getRouteApi, Link } from '@tanstack/react-router';
import { useAuthData, useScopes } from '~/components/Providers/AuthProvider';
import { useState } from 'react';
import { EditClanSheet } from './-EditClanSheet';
import { EditClanMemberSheet } from './-EditClanMemberSheet';
import { AddClanMemberSheet } from './-AddClanMemberSheet';
import { UserProfilePicture } from '~/components/UserProfilePicture';
import { CountryClanPointDisplay, RankDisplay } from '../-shared';

const Route = getRouteApi('/profile/clan/$clanid');

function ClanMemberDisplay({
    editable,
    onEdit,
    ...member
}: {
    editable: boolean;
    onEdit: () => void;
} & ClanMemberWithContributedPoints) {
    return (
        <Link
            to="/profile/user/$userid"
            params={{ userid: member.username }}
            className="inline-flex items-center flex-none hover:scale-105 transition-transform cursor-pointer p-2 rounded-lg"
        >
            <UserProfilePicture user={member} className="size-10" />
            <div className="ml-4">
                <div className="flex items-center gap-2">
                    {member.role === 2 ? (
                        <Crown
                            color="hsl(var(--primary))"
                            fill="hsl(var(--primary))"
                        />
                    ) : member.role === 1 ? (
                        <Crown color="hsl(var(--primary))" />
                    ) : null}
                    <p className="truncate text-xl ">{member.global_name}</p>
                </div>

                <p className="text-md text-gray-500">
                    {member.contributed_points && member.contributed_points > 0
                        ? `+${Math.round(member.contributed_points) / 10} points`
                        : ''}
                </p>
            </div>
            {editable && (
                <Pencil
                    className="h-5 w-5 ml-2 text-secondary-foreground hover:text-secondary-light cursor-pointer transition-colors"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onEdit();
                    }}
                />
            )}
        </Link>
    );
}

function ClanProfileSummary() {
    const { profile, members, classicTotal, platformerTotal } =
        Route.useLoaderData({
            select: ({ profile, members, classicTotal, platformerTotal }) => ({
                profile,
                members,
                classicTotal,
                platformerTotal,
            }),
        });

    const membersMap = profile?.members_points?.reduce(
        (accumulator, { member, contributed_points }) => {
            const existingMember = accumulator.get(member.id);
            if (existingMember) {
                existingMember.contributed_points = contributed_points;
            }
            return accumulator;
        },
        new Map<string, ClanMemberWithContributedPoints>(
            members?.map((member) => [member.id, { ...member }]),
        ),
    );

    const membersWithContributedPoints = Array.from(membersMap?.values() || []);

    membersWithContributedPoints.sort((firstMember, secondMember) => {
        if (secondMember.role !== firstMember.role) {
            return secondMember.role - firstMember.role;
        }
        return (
            (secondMember.contributed_points || 0) -
            (firstMember.contributed_points || 0)
        );
    });

    const { hasScope } = useScopes();
    const editable = hasScope('clan_modify');
    const [editOpen, setEditOpen] = useState(false);
    const [editMemberOpen, setEditMemberOpen] = useState(false);
    const [addMemberOpen, setAddMemberOpen] = useState(false);
    const [memberToEdit, setMemberToEdit] = useState<ClanMember | null>(null);

    const { primaryColor } = useTheme();
    const hue = chroma(primaryColor).get('hsl.h');
    const lightness = chroma(primaryColor).get('hsl.l') * 100 + 50;

    const authedUserClan = useAuthData()?.clan;

    return (
        <div className="flex flex-col w-full lg:w-2/6 lg:px-2">
            <ScrollArea className="lg:p-4 lg:h-[calc(100vh-150px)]">
                <div className="flex flex-col items-center text-center w-full">
                    <div className="flex items-center justify-center gap-2 mb-1">
                        <p className="clamp-[text-2xl-4xl-clamp] lg:clamp-[text-2xl-3xl-clamp] leading-[1.5!important] font-medium text-secondary-foreground">
                            [{profile?.clan.tag}]
                            {profile?.clan.tag !== profile?.clan.global_name &&
                                ' ' + profile?.clan.global_name}
                        </p>{' '}
                        {editable && (
                            <Pencil
                                className="size-7 text-secondary-foreground hover:text-secondary-light cursor-pointer transition-colors"
                                onClick={() => setEditOpen(true)}
                            />
                        )}
                    </div>
                </div>

                <p className="text-center text-secondary-light">
                    {profile?.clan.description ??
                        "This clan hasn't provided a description yet"}
                </p>

                <ListTabSwitch
                    from="/profile/clan/$clanid"
                    className="w-10/12 mx-auto"
                    classicDisplay={`Classic (${classicTotal})`}
                    platformerDisplay={`Platformer (${platformerTotal})`}
                    size="md"
                />

                <div className="flex justify-evenly items-center mt-6">
                    <CountryClanPointDisplay
                        label="Level Points"
                        value={profile?.rank?.level_points / 10}
                    />
                </div>

                <div className="mt-8 text-center clamp-[px-3-6-clamp]">
                    <h3 className="clamp-[text-2xl-4xl-clamp] lg:clamp-[text-lg-2xl-clamp] font-semibold uppercase">
                        <ThemedImage
                            src="/assets/globalRank.webp"
                            alt="global rank icon"
                            className="inline h-6 mr-2"
                            hueRotation={hue}
                            lightnessCorrection={lightness}
                        />
                        Clan Rankings
                    </h3>
                    <div className="mt-2">
                        <RankDisplay
                            label="Points Rank"
                            value={profile?.rank?.rank}
                        />
                        <RankDisplay
                            label="Extremes Count Rank"
                            value={profile?.rank?.extremes_rank}
                        />
                        <RankDisplay
                            label="Hardest Level Rank"
                            value={profile?.rank?.hardest_rank}
                        />
                    </div>
                </div>
                <h2 className="clamp-[text-2xl-4xl-clamp] lg:clamp-[text-lg-2xl-clamp] font-semibold uppercase text-center my-4">
                    Members ({members?.length})
                </h2>
                <div className="flex w-full flex-wrap justify-center gap-x-8 gap-y-4 pr-4">
                    {membersWithContributedPoints?.map((member) => (
                        <ClanMemberDisplay
                            key={member.id}
                            {...member}
                            editable={editable}
                            onEdit={() => {
                                setMemberToEdit(member);
                                setEditMemberOpen(true);
                            }}
                        />
                    ))}
                </div>

                <div className="flex flex-col items-center text-center mt-4">
                    {editable && (
                        <button
                            onClick={() => setAddMemberOpen(true)}
                            className={cn(
                                'self-center w-fit p-2 rounded-md my-1 mb-4',
                                'flex gap-x-2 items-center transition-colors cursor-pointer',
                                'bg-background-lighttransparent hover:bg-secondary-light',
                                'outline-dotted outline-neutral-700',
                            )}
                        >
                            <Plus className="self-center" />
                            <p className="text-start">Add member...</p>
                        </button>
                    )}

                    {authedUserClan?.id == profile?.clan.id && (
                        <Button asChild variant={'outline'} className="m-2">
                            <Link to="/clan">
                                <Settings size={20} className="mr-2" /> Clan
                                Settings
                            </Link>
                        </Button>
                    )}

                    <Button asChild variant={'outline'} className="m-2">
                        <Link
                            to="/leaderboard"
                            search={(prev) => ({
                                list: prev.list,
                                page: 1,
                                type: 'users',
                                clan: profile?.clan,
                            })}
                            preload="intent"
                            preloadDelay={200}
                        >
                            <SquareArrowRightIcon size={20} className="mr-2" />
                            Clan members leaderboard
                        </Link>
                    </Button>
                </div>
            </ScrollArea>
            {editable && (
                <>
                    <EditClanSheet open={editOpen} onOpenChange={setEditOpen} />{' '}
                    <AddClanMemberSheet
                        open={addMemberOpen}
                        onOpenChange={setAddMemberOpen}
                    />
                    {memberToEdit && (
                        <EditClanMemberSheet
                            open={editMemberOpen}
                            onOpenChange={setEditMemberOpen}
                            member={memberToEdit}
                        />
                    )}
                </>
            )}
        </div>
    );
}

export default ClanProfileSummary;
